<template>
	<div
		v-if="!hasNoResults || loadingUsersAndGroups"
		:class="{
			'user-assign-select__dropdown--disabled': !isModifyEnabled,
		}"
	>
		<SuiSelectListHelpers
			v-if="!isSingleUserAssign && !isRoleFilterActive"
			class="user-assign-select__select-all-wrapper"
		>
			<SuiSelectListHelpersSelectAll
				:checked="isAllUsersSelected"
				:select-all-text="$t('select_list_users_groups_select_all')"
				:aria-label="$t('user_assign_select_all_label')"
				@input="$emit('select-all', $event)"
			/>

			<div class="user-assign-select__actions">
				<SuiSelectListHelpersAssignTo
					v-if="shouldShowAssignToMe"
					:url="currentUser?.profilePicture?.mini"
					:initials="currentUser?.initials ?? ''"
					:color="currentUser?.hslColor ?? ''"
					:tooltip="$t('user_assign_select_assign_to_me')"
					@click="handleAssignToMe"
				/>

				<SuiSelectListHelpersDropdown
					v-if="userGroupList.length > 0 || loadingUsersAndGroups"
					:value="selectedUserGroup"
					:groups="userGroupList"
					:loading="loadingUsersAndGroups"
					:loading-label="$t('spa__general.loading')"
					:aria-label="$t('select_list_users_groups_groups')"
					:popover-props="{
						placement: 'bottom-end',
						distance: 50,
						skidding: 120,
						referenceNode: headerElement,
					}"
					:hide-on-trigger-invisible="false"
					@toggle-on="$emit('select-user-group', $event)"
					@toggle-off="$emit('deselect-user-group', $event)"
				/>
			</div>
		</SuiSelectListHelpers>

		<div v-bind="virtualListAttrs">
			<UserAssignSelectListGroup
				v-for="group in groupedVirtualList"
				:key="group.id"
				:group="group"
				:is-closed="closedGroups.includes(group.id)"
				:selected-users="selectedUsers"
				:selected-roles="selectedRoles"
				:is-single-user-assign="isSingleUserAssign"
				:task-users-availability="taskUsersAvailability"
				:active-role-filter-id="activeRoleFilterId"
				:measure-element="measureElement"
				@toggle-group-dropdown="
					(target, group) =>
						$emit('toggle-group-dropdown', target, group)
				"
				@toggle-all-users-selected="
					(group, value) =>
						$emit('toggle-all-users-selected', group, value)
				"
				@user-clicked="(userId) => $emit('user-clicked', userId)"
				@role-clicked="(role) => $emit('role-clicked', role)"
			/>
		</div>

		<UserAssignSelectListSkeleton
			v-if="loadingUsersAndGroups"
			show-group-wrapper
		/>
	</div>
	<UserAssignSelectListSkeleton
		v-if="hasNoResults && loadingUsersAndGroups"
		show-group-wrapper
	/>
	<SuiSelectListEmpty v-else-if="hasNoResults && !loadingUsersAndGroups">
		{{ $t('search.noResults') }}
	</SuiSelectListEmpty>
</template>

<script setup lang="ts">
import { type HTMLAttributes, computed } from 'vue';
import {
	SuiSelectListHelpers,
	SuiSelectListHelpersSelectAll,
	SuiSelectListHelpersAssignTo,
	SuiSelectListHelpersDropdown,
	SuiSelectListEmpty,
	type SuiSelectListGroupType,
	type SuiSelectListItemsAndGroupsValueType,
} from '@developers/scoro-ui';
import {
	type User,
	type UserAssignSelectUserFragment,
	type UserAssignSelectAvailabilityQuery,
} from '@/generated/graphql';
import { Setting } from '@/constants/settings';
import { useBooleanSetting } from '@/composables/settings/useSettings';
import { useCurrentUser } from '@/features/Auth';
import UserAssignSelectListGroup from './UserAssignSelectListGroup.vue';
import UserAssignSelectListSkeleton from './UserAssignSelectListSkeleton.vue';
import {
	type UserAssignGroup,
	type UserAssignSelectRole,
} from '../../../types';

export interface UserAssignSelectListProps {
	filteredUserList: UserAssignSelectUserFragment[];
	isModifyEnabled: boolean;
	isSingleUserAssign: boolean;
	isAllUsersSelected: boolean;
	selectedUsers: Array<User['id']>;
	selectedRoles: UserAssignSelectRole[];
	userGroupList: SuiSelectListGroupType[];
	selectedUserGroup: SuiSelectListItemsAndGroupsValueType;
	loadingUsersAndGroups: boolean;
	closedGroups: UserAssignGroup['id'][];
	headerElement: () => Element | null;
	taskUsersAvailability: UserAssignSelectAvailabilityQuery['TaskUsersAvailability'];
	activeRoleFilterId: UserAssignSelectRole['id'] | null;
	groupedVirtualList: UserAssignGroup[];
	virtualListAttrs: HTMLAttributes;
	measureElement: (element: unknown) => void;
}

const props = defineProps<UserAssignSelectListProps>();

const emit = defineEmits<{
	'select-all': [value: boolean];
	'select-user-group': [groupId: string];
	'deselect-user-group': [groupId: string];
	'toggle-group-dropdown': [target: HTMLElement, group: UserAssignGroup];
	'toggle-all-users-selected': [group: UserAssignGroup, value: boolean];
	'user-clicked': [userId: User['id']];
	'role-clicked': [role: UserAssignSelectRole];
}>();

const [assignRolesToTasksSetting] = useBooleanSetting(
	Setting.ASSIGN_ROLES_TO_TASKS
);

const { currentUser } = useCurrentUser();

const hasNoResults = computed(() => {
	if (assignRolesToTasksSetting.value) {
		return props.groupedVirtualList.length === 0;
	}
	return props.filteredUserList.length === 0;
});

const isRoleFilterActive = computed(() => {
	return props.activeRoleFilterId !== null;
});

const isCurrentUserSelected = computed(() => {
	if (!currentUser.value?.id) {
		return false;
	}
	return props.selectedUsers.includes(currentUser.value.id);
});

const shouldShowAssignToMe = computed(() => !isCurrentUserSelected.value);

const handleAssignToMe = () => {
	if (currentUser.value?.id) {
		emit('user-clicked', currentUser.value.id);
	}
};
</script>

<style scoped lang="scss">
.user-assign-select {
	&__actions {
		display: flex;
		align-items: center;
		gap: 0.5rem;
	}
}
</style>
